import { useState, useEffect, useMemo } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { DillButton, DillInput, DillField, DillToggle } from '../../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { updateSupplier } from '../../../../../redux/services/suppliersService';
import { openDillToast } from '../../../../../utils/helpers';
import { Supplier } from '@dill/dill-shared';

const SectionCard = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4">{children}</div>
);

export default function TradeReferenceSettings({ supplier }: { supplier?: Supplier | null }) {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [tradeRefSettings, setTradeRefSettings] = useState({
    autoSend: false,
    intervalDays: '3',
    maxFollowUps: '3',
  });
  const [savedTradeRefSettings, setSavedTradeRefSettings] = useState({
    autoSend: false,
    intervalDays: '3',
    maxFollowUps: '3',
  });
  const [tradeRefErrors, setTradeRefErrors] = useState<{
    intervalDays?: string;
    maxFollowUps?: string;
  }>({});
  const [savingTradeRef, setSavingTradeRef] = useState(false);

  useEffect(() => {
    const setting = supplier?.creditApplicationSetting;
    const loaded = {
      autoSend: setting?.autoSendTradeReference ?? false,
      intervalDays: String(setting?.autoSendTradeReferenceSettings?.numberOfDaysIntervals ?? 3),
      maxFollowUps: String(setting?.autoSendTradeReferenceSettings?.numberOfFollowUps ?? 3),
    };
    setTradeRefSettings(loaded);
    setSavedTradeRefSettings(loaded);
  }, [supplier]);

  const saveTradeRefSettings = async () => {
    const errs: typeof tradeRefErrors = {};
    if (tradeRefSettings.autoSend) {
      const days = parseInt(tradeRefSettings.intervalDays);
      const followUps = parseInt(tradeRefSettings.maxFollowUps);
      if (!tradeRefSettings.intervalDays || isNaN(days) || days < 1)
        errs.intervalDays = 'Must be at least 1';
      if (!tradeRefSettings.maxFollowUps || isNaN(followUps) || followUps < 1)
        errs.maxFollowUps = 'Must be at least 1';
    }
    if (Object.keys(errs).length) {
      setTradeRefErrors(errs);
      return;
    }
    setSavingTradeRef(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id ?? '',
        supplierDetails: {
          creditApplicationSetting: {
            ...supplier?.creditApplicationSetting,
            autoSendTradeReference: tradeRefSettings.autoSend,
            ...(tradeRefSettings.autoSend && {
              autoSendTradeReferenceSettings: {
                numberOfDaysIntervals: parseInt(tradeRefSettings.intervalDays),
                numberOfFollowUps: parseInt(tradeRefSettings.maxFollowUps),
              },
            }),
          },
        },
      })
    );
    setSavingTradeRef(false);
    if (result.type.includes('fulfilled')) {
      setSavedTradeRefSettings(tradeRefSettings);
      openDillToast({ type: 'SUCCESS', message: 'Trade reference settings saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save settings.' });
    }
  };

  const tradeRefDirty =
    tradeRefSettings.autoSend !== savedTradeRefSettings.autoSend ||
    tradeRefSettings.intervalDays !== savedTradeRefSettings.intervalDays ||
    tradeRefSettings.maxFollowUps !== savedTradeRefSettings.maxFollowUps;

  return (
    <SectionCard>
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-sm font-semibold text-gray-900 m-0">Trade Reference Follow-ups</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Automatically send follow-up emails to trade references that haven't responded.
          </p>
        </div>

        <div className="flex items-center justify-between py-3 border-b border-gray-100">
          <div>
            <p className="text-xs font-semibold text-gray-700 m-0">Enable automatic follow-ups</p>
            <p className="text-[11px] text-gray-400 mt-0.5 m-0">
              Send reminders when references don't respond
            </p>
          </div>
          <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
            <DillToggle
              value={tradeRefSettings.autoSend}
              onChange={(v) => {
                setTradeRefSettings((p) => ({ ...p, autoSend: v }));
                setTradeRefErrors({});
              }}
              isDisabled={!canEdit}
            />
          </div>
        </div>

        {tradeRefSettings.autoSend && (
          <div className="grid grid-cols-2 gap-4">
            <DillField label="Follow-up interval (days)" error={tradeRefErrors.intervalDays}>
              <DillInput
                type="number"
                value={tradeRefSettings.intervalDays}
                onChange={(v) => {
                  setTradeRefSettings((p) => ({ ...p, intervalDays: v }));
                  setTradeRefErrors((p) => ({ ...p, intervalDays: undefined }));
                }}
                onBlur={(v) =>
                  setTradeRefSettings((p) => ({
                    ...p,
                    intervalDays: isNaN(parseFloat(v)) ? '' : parseFloat(v).toFixed(0),
                  }))
                }
                placeholder="e.g. 3"
                error={tradeRefErrors.intervalDays}
                disabled={!canEdit}
              />
            </DillField>
            <DillField label="Max follow-ups" error={tradeRefErrors.maxFollowUps}>
              <DillInput
                type="number"
                value={tradeRefSettings.maxFollowUps}
                onChange={(v) => {
                  setTradeRefSettings((p) => ({ ...p, maxFollowUps: v }));
                  setTradeRefErrors((p) => ({ ...p, maxFollowUps: undefined }));
                }}
                onBlur={(v) =>
                  setTradeRefSettings((p) => ({
                    ...p,
                    maxFollowUps: isNaN(parseFloat(v)) ? '' : parseFloat(v).toFixed(0),
                  }))
                }
                placeholder="e.g. 3"
                error={tradeRefErrors.maxFollowUps}
                disabled={!canEdit}
              />
            </DillField>
          </div>
        )}

        <div
          className="flex justify-end pt-1"
          data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
          <DillButton
            label="Save Settings"
            onClick={saveTradeRefSettings}
            isLoading={savingTradeRef}
            isDisabled={!canEdit || !tradeRefDirty || savingTradeRef}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </SectionCard>
  );
}
